import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, TrendingUp, ArrowRight, CheckCircle, Loader2, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ASSET_TYPES = ["Multifamily", "Retail", "Industrial", "Mixed-Use", "Office", "Other"];

// Market cap-rate range used for the preliminary value estimate
const CAP_LOW = 0.055;
const CAP_HIGH = 0.075;

const fmtUSD = (n) => {
  if (!isFinite(n) || n <= 0) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}K`;
  return `$${Math.round(n)}`;
};

export default function OfferEstimator() {
  const [assetType, setAssetType] = useState("Multifamily");
  const [noi, setNoi] = useState("");
  const [asking, setAsking] = useState("");
  const [result, setResult] = useState(null);

  const [lead, setLead] = useState({ name: "", email: "", phone: "", address: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const calculate = (e) => {
    e.preventDefault();
    const noiNum = parseFloat(String(noi).replace(/[^0-9.]/g, ""));
    if (!noiNum || noiNum <= 0) return;
    const askNum = parseFloat(String(asking).replace(/[^0-9.]/g, "")) || 0;
    const valLow = noiNum / CAP_HIGH;   // higher cap = lower value
    const valHigh = noiNum / CAP_LOW;   // lower cap = higher value
    const impliedCap = askNum > 0 ? (noiNum / askNum) * 100 : null;
    let verdict = null;
    if (impliedCap !== null) {
      if (impliedCap >= CAP_HIGH * 100) verdict = { tone: "good", text: "Attractively priced for a buyer — strong yield." };
      else if (impliedCap >= CAP_LOW * 100) verdict = { tone: "ok", text: "In line with current market cap rates." };
      else verdict = { tone: "high", text: "Priced aggressively vs. market — but we may still have structure that works." };
    }
    setResult({ valLow, valHigh, impliedCap, verdict });
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/api/sellers/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: lead.name, email: lead.email, phone: lead.phone, address: lead.address,
          property_type: assetType, asking_price: asking, annual_noi: noi,
          make_offer: true,
          notes: `Instant Estimator request. Estimated value range ${fmtUSD(result.valLow)}–${fmtUSD(result.valHigh)}.`,
        }),
      });
      if (res.ok) setSent(true);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const inputCls = "w-full rounded-xl border border-emerald-700/40 bg-emerald-950/40 px-4 py-3 text-sm text-cream placeholder-cream/40 outline-none transition-colors focus:border-gold/60 focus:ring-1 focus:ring-gold/30";
  const labelCls = "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-gold/80";

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#022c22] to-emerald-950 py-16 sm:py-24" data-testid="offer-estimator">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: "40px 40px" }} />
      <div className="pointer-events-none absolute -left-32 top-0 h-[400px] w-[400px] rounded-full bg-gold/10 blur-[130px]" />

      <div className="relative z-10 mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center">
          <div className="inline-flex items-center gap-2 text-gold">
            <Calculator className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.35em]">Instant Estimate</span>
          </div>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-cream sm:text-4xl md:text-5xl">
            What's Your Property <span className="luxury-text-gradient">Worth?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-cream/70 sm:text-base">
            Enter your numbers for a preliminary value range based on current market cap rates. No obligation, no pressure.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 rounded-3xl border border-emerald-700/40 bg-emerald-900/30 p-6 backdrop-blur-md sm:p-8">
          {/* Inputs */}
          <form onSubmit={calculate} className="grid gap-5 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Asset Type</label>
              <select value={assetType} onChange={(e) => setAssetType(e.target.value)} className={inputCls} data-testid="estimator-asset-type">
                {ASSET_TYPES.map((t) => <option key={t} value={t} className="bg-emerald-950">{t}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Annual NOI ($)</label>
              <input value={noi} onChange={(e) => setNoi(e.target.value)} placeholder="e.g. 450,000" className={inputCls} data-testid="estimator-noi" inputMode="numeric" />
            </div>
            <div>
              <label className={labelCls}>Asking Price ($) <span className="text-cream/40 normal-case">— optional</span></label>
              <input value={asking} onChange={(e) => setAsking(e.target.value)} placeholder="e.g. 6,000,000" className={inputCls} data-testid="estimator-asking" inputMode="numeric" />
            </div>
            <div className="sm:col-span-3">
              <button type="submit" data-testid="estimator-calculate"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3.5 text-sm font-bold text-emerald-950 transition-all hover:shadow-[0_8px_30px_rgba(229,201,139,0.35)] sm:w-auto">
                Calculate Estimate
                <TrendingUp className="h-4 w-4" />
              </button>
            </div>
          </form>

          {/* Result */}
          <AnimatePresence>
            {result && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                className="mt-8 border-t border-emerald-700/40 pt-8" data-testid="estimator-result">
                <div className="text-center">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-gold/80">Estimated Market Value Range</div>
                  <div className="mt-2 text-3xl font-bold text-cream sm:text-4xl md:text-5xl">
                    <span className="luxury-text-gradient">{fmtUSD(result.valLow)}</span>
                    <span className="mx-2 text-cream/40">–</span>
                    <span className="luxury-text-gradient">{fmtUSD(result.valHigh)}</span>
                  </div>
                  <div className="mt-2 text-xs text-cream/50">Based on a {(CAP_LOW * 100).toFixed(1)}%–{(CAP_HIGH * 100).toFixed(1)}% market cap rate</div>
                  {result.impliedCap !== null && (
                    <div className={`mt-4 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold ${
                      result.verdict.tone === "good" ? "bg-emerald-500/20 text-emerald-300"
                      : result.verdict.tone === "ok" ? "bg-gold/20 text-gold"
                      : "bg-amber-500/20 text-amber-300"}`}>
                      <Info className="h-3.5 w-3.5" />
                      Your asking implies a {result.impliedCap.toFixed(1)}% cap rate — {result.verdict.text}
                    </div>
                  )}
                </div>

                {/* Lead capture */}
                {sent ? (
                  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="mt-8 rounded-2xl bg-emerald-500/10 p-6 text-center" data-testid="estimator-sent">
                    <CheckCircle className="mx-auto h-10 w-10 text-emerald-400" />
                    <h3 className="mt-3 text-lg font-bold text-cream">Request Received</h3>
                    <p className="mt-1 text-sm text-cream/70">Our acquisitions team will prepare a formal, no-obligation written offer and reach out within 48 hours.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={submitLead} className="mt-8 rounded-2xl bg-emerald-950/40 p-6">
                    <p className="mb-4 text-center text-sm font-semibold text-cream">Want a real, written offer? Tell us where to send it.</p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <input required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Full name" className={inputCls} data-testid="estimator-lead-name" />
                      <input required type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="estimator-lead-email" />
                      <input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="Phone (optional)" className={inputCls} data-testid="estimator-lead-phone" />
                      <input value={lead.address} onChange={(e) => setLead({ ...lead, address: e.target.value })} placeholder="Property address" className={inputCls} data-testid="estimator-lead-address" />
                    </div>
                    <button type="submit" disabled={sending} data-testid="estimator-lead-submit"
                      className="group mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3.5 text-sm font-bold text-emerald-950 transition-all hover:shadow-[0_8px_30px_rgba(229,201,139,0.35)] disabled:opacity-60">
                      {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Get My Formal Offer <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
                    </button>
                  </form>
                )}

                <p className="mt-5 text-center text-[11px] leading-5 text-cream/40">
                  This is a preliminary estimate generated from the figures you provided and current market cap rates — not a formal offer or appraisal. Actual offers depend on full underwriting and due diligence.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
